// SB6-ANA-FILT-06 (Track C) — AI Filter Module state.
//
// The AI Filter Module lets the user, per AI class, (a) check the class
// in/out and (b) set a per-class minimum-confidence percent (1..99,
// default 35 — D7). The widget computes the live "# findings ≥ threshold"
// client-side from each class's confidence histogram (D8), so dragging
// the slider never fires a request.
//
// Two pieces of shared state live here so they survive the widget being
// unmounted (the widget starts hidden, opened on demand) and so the
// effective AI sub-filter can be read by the data-point / facet query
// hooks WITHOUT every call site knowing about the AI widget:
//
//   * `config` — the live per-class { checked, minPct } map the widget
//     edits, and
//   * `committedFilter` — the debounced `aiClassFilter` JSON string the
//     query hooks fold into the data-points + facets requests so the L3
//     AI count + the downstream cascade reflect the active AI config
//     (D6). It is the JSON map `{ "<classUuid>": <minPct> }` over the
//     CHECKED classes only, emitted on commit when the AI kind is active;
//     `null` when the AI sub-filter must NOT narrow (inactive, or no
//     class checked).
//
// The COMMIT is debounced (the widget owns the 300ms timer); the live
// per-class count is debounce-free (pure histogram math in the widget).
import { create } from "zustand";
import { useDataPointFilterStore } from "./dataPointFilterStore.js";

/** Per-class config row the AI widget edits. */
export interface AiClassConfig {
  /** Whether this class participates in the AI sub-filter. */
  checked: boolean;
  /** Per-class minimum-confidence percent, integer 1..99 (D7). */
  minPct: number;
}

/** Default per-class min-confidence percent (D7). */
export const DEFAULT_AI_MIN_CONFIDENCE_PCT = 35;

/** Confidence slider bounds (D7). */
export const AI_MIN_CONFIDENCE = 1;
export const AI_MAX_CONFIDENCE = 99;

/**
 * Clamp an arbitrary number to an integer percent within the slider
 * bounds (no decimals end-to-end — D7).
 */
export function clampConfidencePct(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_AI_MIN_CONFIDENCE_PCT;
  const rounded = Math.round(value);
  if (rounded < AI_MIN_CONFIDENCE) return AI_MIN_CONFIDENCE;
  if (rounded > AI_MAX_CONFIDENCE) return AI_MAX_CONFIDENCE;
  return rounded;
}

/**
 * Build the `aiClassFilter` wire JSON from the live config. Only CHECKED
 * classes are included (unchecked ⇒ excluded entirely — the BE drops a
 * class absent from the map). Returns `null` when nothing is checked, so
 * the caller can omit the param (the AI side then passes all findings).
 */
export function buildAiClassFilterJson(
  config: Readonly<Record<string, AiClassConfig>>,
): string | null {
  const map: Record<string, number> = {};
  for (const [classUuid, row] of Object.entries(config)) {
    if (row.checked) map[classUuid] = clampConfidencePct(row.minPct);
  }
  if (Object.keys(map).length === 0) return null;
  return JSON.stringify(map);
}

interface AiFilterState {
  /** Live per-class config keyed by `class_uuid`. */
  config: Record<string, AiClassConfig>;
  /**
   * Debounced `aiClassFilter` JSON over the checked classes; `null` when
   * the AI sub-filter must not narrow. Read by the query hooks.
   */
  committedFilter: string | null;
  /** Set (or initialise) one class's config row. */
  setClass: (classUuid: string, next: AiClassConfig) => void;
  /** Replace the whole config map (used when (un)checking many at once). */
  setConfig: (next: Record<string, AiClassConfig>) => void;
  /** Set the committed `aiClassFilter` JSON (called on the debounced commit). */
  setCommittedFilter: (json: string | null) => void;
}

export const useAiFilterStore = create<AiFilterState>((set) => ({
  config: {},
  committedFilter: null,
  setClass: (classUuid, next) => {
    set((s) => ({ config: { ...s.config, [classUuid]: next } }));
  },
  setConfig: (next) => {
    set({ config: next });
  },
  setCommittedFilter: (json) => {
    // SB6-ANA-PROG-02 — a committed AI-config change refires the
    // data-point + facet queries (the AI filter is folded into their
    // params). Bump the shared filter-revision so dependent widgets read
    // as out-of-sync until the new payload lands. Only bump on a real
    // change so re-committing the same value (e.g. an idempotent debounce
    // tick) doesn't spuriously mark everything stale.
    set((s) => {
      if (s.committedFilter === json) return s;
      useDataPointFilterStore.getState().bumpRevision();
      return { committedFilter: json };
    });
  },
}));
